import React from "react";
import IconButton from "@material-ui/core/IconButton";
import Tooltip from "@material-ui/core/Tooltip";
import FileCopyIcon from "@material-ui/icons/FileCopy";
import { useDispatch } from "react-redux";
import { showErrorMessage, showSuccessMessage } from "../../actions";
import { injectIntl, FormattedMessage } from "react-intl";
import { normalizeClipboardValue } from "../../util/clipboardFormat";

const SAFE_TAKE = 100000;

const getRowsByResult = (result, path) => {
  if (Array.isArray(result)) return result;

  if (path && result && Array.isArray(result[path])) {
    return result[path];
  }

  return [];
};

const getValue = (col, row) => {
  if (col?.exportValue) {
    return col.exportValue(row);
  }

  return row?.[col.field];
};

/**
 * Componente de botão para copiar dados em formato CSV para a área de transferência. Ele pode ser usado em tabelas ou relatórios para permitir que os usuários copiem os dados exibidos.
 * Props:
 * - columns: Array de objetos que definem as colunas a serem copiadas. Cada objeto deve conter pelo menos um campo 'field' que corresponde à chave dos dados e pode conter um campo 'exportValue' para personalizar o valor a ser copiado.
 * - data: Array de objetos que representam os dados atualmente exibidos. Usado quando fetchAllData não é fornecido.
 * - fetchAllData: Função assíncrona que busca todos os dados para serem copiados. Se fornecida, será utilizada em vez de 'data'.
 * - params: Parâmetros para a função fetchAllData.
 * - intl: Instância do intl para internacionalização.
 * - titleField: Nome do campo que contém o título da coluna.
 * - disabled: Indica se o botão deve estar desabilitado.
 * - rowsPath: Caminho para acessar as linhas de dados dentro do resultado de fetchAllData, em casos que não sejam arrays diretos.
*/
const CopyCsvButton = ({ columns = [], data = [], fetchAllData, params, intl, disabled, rowsPath }) => {
  const [loading, setLoading] = React.useState(false);
  const dispatch = useDispatch();

  const toClipboardTable = (rows, cols) => {
    if (!rows.length || !cols.length) return "";

    const sep = "\t";

    const headers = cols.map((c) => intl.formatMessage({ id: c?.titleRaw || c.title || c.label }));
    const lines = rows.map((row) => cols.map((col) => normalizeClipboardValue(getValue(col, row))).join(sep));

    return [headers.join(sep), ...lines].join("\r\n");
  };

const writeToClipboard = async (textPromise) => {
    if (navigator.clipboard.write && window.ClipboardItem) {
      return navigator.clipboard.write([
        new window.ClipboardItem({
          "text/plain": textPromise.then(
            (text) => new Blob([text], { type: "text/plain" }),
          ),
        }),
      ]);
    }

    const text = await textPromise;
    return navigator.clipboard.writeText(text);
  };

  const handleCopy = async () => {
    try {
      setLoading(true);

      const textPromise = (async () => {
        const result = fetchAllData
          ? await fetchAllData({ skip: 0, take: SAFE_TAKE, params })
          : data;

        const rows = getRowsByResult(result, rowsPath);

        if (!rows?.length || !columns?.length) {
          return "";
        }

        return toClipboardTable(rows, columns);
      })();

      await writeToClipboard(textPromise);

      dispatch(showSuccessMessage("app.action.copied"));
    } catch {
      dispatch(showErrorMessage("app.action.copy.error"));
    } finally {
      setLoading(false);
    }
  };

  return (
    <Tooltip title={<FormattedMessage id={loading ? "app.loading.fetching" : "app.action.copy.label"} />}>
      <span>
        <IconButton
          disabled={disabled || loading}
          onClick={handleCopy}
        >
          <FileCopyIcon />
        </IconButton>
      </span>
    </Tooltip>
  );
};
export default injectIntl(CopyCsvButton);
